import { ReactNode, Component, ErrorInfo } from 'react';
import { TErrorEventMap, THandleCoreError, useCoreError } from '../errors';

interface Props {
  children?: ReactNode;
  consumerApp: string;
  feedbackUIAvailability: boolean;
  errorMap?: TErrorEventMap;
  errorMapOverride?: THandleCoreError['errorMapOverride'];
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error: error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error(this.props.consumerApp, error, errorInfo);
  }

  render() {
    const { hasError, error } = this.state;
    if (hasError && error) {
      const { feedbackUIAvailability, consumerApp, errorMap, errorMapOverride } = this.props;
      return (
        <BoundaryErrorHandler
          feedbackUIAvailability={feedbackUIAvailability}
          consumer={consumerApp}
          error={error}
          errorMap={errorMap || null}
          errorMapOverride={errorMapOverride || null}
        />
      );
    }
    return this.props.children;
  }
}

export { ErrorBoundary };

type TBoundaryErrorHandler = {
  feedbackUIAvailability: boolean;
  consumer: string;
  error: Error;
  errorMap: TErrorEventMap | null;
  errorMapOverride?: THandleCoreError['errorMapOverride'] | null;
};

const BoundaryErrorHandler = ({
  feedbackUIAvailability,
  consumer,
  error,
  errorMap,
  errorMapOverride
}: TBoundaryErrorHandler) => {
  const handleCoreError = useCoreError();

  handleCoreError({
    feedbackUIAvailability,
    consumer,
    error,
    errorMap: {
      ...(errorMap || {}),
    },
    errorMapOverride: {...errorMapOverride || {type: 'page'}},
  });

  return null;
};
